import { useCallback, useEffect, useState } from 'react';
import type { MemoryItem, Output, ProjectContextVersion, StateDoc, StateDocKind } from '@jabberr/core';
import { api } from '../lib/api.js';

/**
 * What Jabberr knows about a project, gathered for the project page, the knows page and the conversation's
 * side panel. Every part is fetched on its own and is null when it could not be read: the server decides what a
 * guest may see (production state only, no project memory, no outputs), so a guest simply gets less back.
 */
export interface Knows {
  state: Partial<Record<StateDocKind, StateDoc>> | null;
  /** Latest internal project context ("Decisions and plans"); null for guests and on error. */
  context: ProjectContextVersion | null;
  memory: { project: MemoryItem[]; user: MemoryItem[] } | null;
  outputs: Output[] | null;
}

const EMPTY: Knows = { state: null, context: null, memory: null, outputs: null };

export function useKnows(projectId: string, opts: { team: boolean; outputs?: boolean } = { team: true }): Knows & { loading: boolean; reload: () => void; setMemory: (m: Knows['memory']) => void } {
  const [knows, setKnows] = useState<Knows>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const { team, outputs: wantOutputs = true } = opts;

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    const state = api.state
      .get(projectId)
      .then(({ docs }) => Object.fromEntries(docs.map((d) => [d.kind, d])) as Knows['state'])
      .catch(() => null);
    const context = team ? api.context.get(projectId, 'internal').then((r) => r.latest).catch(() => null) : Promise.resolve(null);
    const memory = team ? api.memory.list(projectId).catch(() => null) : Promise.resolve(null);
    const outputs = team && wantOutputs ? api.outputs.list(projectId).then((r) => r.outputs).catch(() => null) : Promise.resolve(null);
    void Promise.all([state, context, memory, outputs]).then(([s, c, m, o]) => {
      if (!alive) return;
      setKnows({ state: s, context: c, memory: m, outputs: o });
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [projectId, team, wantOutputs, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setMemory = useCallback((memory: Knows['memory']) => setKnows((k) => ({ ...k, memory })), []);
  return { ...knows, loading, reload, setMemory };
}

/** Bullets under one "## Heading" of the project context, as plain lines ("- x" becomes "x"). */
export function contextBullets(content: string | null | undefined, heading: string): string[] {
  if (!content) return [];
  const lines = content.split('\n');
  const start = lines.findIndex((l) => l.trim().toLowerCase() === `## ${heading.toLowerCase()}`);
  if (start < 0) return [];
  const out: string[] = [];
  for (const line of lines.slice(start + 1)) {
    if (/^#{1,2}\s/.test(line)) break;
    const m = /^\s*[-*]\s+(.*\S)\s*$/.exec(line);
    if (m?.[1]) out.push(m[1]);
  }
  return out;
}

/** A state document split at its "## " headings, for a list of short cards. Text before the first one is the lede. */
export function docSections(content: string): { lede: string; sections: { title: string; body: string }[] } {
  const parts = content.split(/^##\s+/m);
  const first = (parts.shift() ?? '').replace(/^#\s+.*$/m, '').trim();
  const sections = parts.map((p) => {
    const nl = p.indexOf('\n');
    const title = (nl < 0 ? p : p.slice(0, nl)).trim();
    const body = (nl < 0 ? '' : p.slice(nl + 1)).trim();
    return { title, body };
  });
  return { lede: first, sections };
}

/** Active memory, newest first; retired lines are left for the full Memory page. */
export function activeMemory(items: MemoryItem[] | undefined): MemoryItem[] {
  return (items ?? []).filter((i) => i.status !== 'retired').sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
